<script setup>
// What a result did to the handicaps: one row per team/player with BEFORE → AFTER spelled out,
// the size of the move, and why (reason + the server's rationale note).
// `changes` = FixtureDetailResponse.handicapChanges; `nameFor(change)` resolves the row's name.
import {computed} from 'vue';
import {useHandicaps} from '~/composables/useHandicaps';

const props = defineProps({
  // column header: 'Player' for singles, 'Team' for pairs and squads
  noun: {type: String, default: 'Player'},
  changes: {type: Array, default: () => []},
  settings: {type: Object, default: null},
  nameFor: {type: Function, required: true},
});
const {changeTone, labelFor, reasonLabel, valueTone, weakerSign} = useHandicaps();

const rows = computed(() => props.changes.map((c) => {
  const before = c.oldLabel ?? labelFor(props.settings, c.oldValue ?? 0) ?? 'Scratch'; // unset = scheme zero
  const after = c.newLabel ?? labelFor(props.settings, c.newValue) ?? String(c.newValue);
  const oldValue = c.oldValue ?? 0; // "Not set" plays as scratch / level
  const tone = changeTone(props.settings, oldValue, c.newValue);
  const higherIsStronger = (props.settings?.directionOverride ?? props.settings?.scheme?.direction) === 1;
  const delta = Number(c.newValue) - Number(oldValue);
  // stronger / weaker is what the reader cares about, not the sign of the number
  const movement = delta === 0 ? 'No change' : ((delta > 0) === higherIsStronger ? 'Harder' : 'Easier');
  // chips are coloured by VALUE (same meaning as on the match card); the movement tag carries harder/easier
  const beforeTone = valueTone(weakerSign(props.settings, oldValue));
  const afterTone = valueTone(weakerSign(props.settings, c.newValue));
  return {id: c.id, name: props.nameFor(c), before, after, tone, beforeTone, afterTone, movement, delta,
    reason: reasonLabel(c.reason), note: c.note};
}));
</script>

<template>
  <div class="hct tl-card">
    <div class="hct-row hct-row--head">
      <span>{{ noun }}</span>
      <span class="hct-col">Before</span>
      <span/>
      <span class="hct-col">After</span>
      <span/>
    </div>
    <div v-for="r in rows" :key="r.id" class="hct-row">
      <span class="hct-name">{{ r.name }}</span>
      <span class="hct-col"><span class="tl-chip hct-chip hct-chip--before" :class="r.beforeTone">{{ r.before }}</span></span>
      <span class="hct-arrow"><v-icon size="16" icon="mdi-arrow-right"/></span>
      <span class="hct-col"><span class="tl-chip hct-chip" :class="r.afterTone">{{ r.after }}</span></span>
      <span class="hct-why">
        <v-menu location="bottom end" :close-on-content-click="false" max-width="360">
          <template #activator="{ props: menu }">
            <button v-bind="menu" type="button" class="tl-chip hct-explain">
              <v-icon size="13" icon="mdi-information-outline"/>Explanation
            </button>
          </template>
          <div class="hct-pop tl-card">
            <div class="hct-move" :class="r.tone">{{ r.movement }}</div>
            <div class="hct-reason">{{ r.reason }}<template v-if="r.note"> · {{ r.note }}</template></div>
          </div>
        </v-menu>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hct { padding: 4px 0; }
// fixed before/after columns so every row lines up under the headings
.hct-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 110px 20px 110px 124px;
  align-items: center;
  gap: 8px 10px;
  padding: 9px 16px;
  font-size: 13px;
  & + & { border-top: 1px solid rgb(var(--v-theme-borderLight)); }
  &--head {
    font-size: 10px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase;
    color: rgb(var(--v-theme-textSubtle)); padding-bottom: 4px;
  }
}
.hct-col { display: flex; justify-content: center; }
.hct-name { font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hct-chip { font-size: 12px; font-weight: 700; padding: 2px 9px; border-radius: 999px; white-space: nowrap; }
.hct-chip--before { opacity: .7; }
.hct-arrow { color: rgb(var(--v-theme-textSubtle)); display: inline-flex; justify-content: center; }
.hct-why { display: flex; justify-content: flex-end; }
.hct-explain { display: inline-flex; align-items: center; gap: 4px; cursor: pointer; border: 1px solid transparent; font: inherit; font-size: 11px; font-weight: 600; &:hover { border-color: rgb(var(--v-theme-borderLight)); } }
.hct-pop { padding: 12px 14px; display: flex; flex-direction: column; gap: 4px; }
.hct-move { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; background: none !important; padding: 0; }
.hct-reason { font-size: 12.5px; color: rgb(var(--v-theme-charcoal)); line-height: 1.4; }
@media (max-width: 600px) {
  .hct-row { grid-template-columns: minmax(0, 1fr) 92px 16px 92px 112px; padding: 9px 10px; gap: 6px; }
}
</style>
